import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { preChatLabel, preChatTexts } from '../utils/preChatText';
import useAiCredits, { creditsOn } from '../hooks/useAiCredits';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faXmark, faSpinner, faComments, faHeadset, faRotateLeft, faLock, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import './chatModal.css';
import './chatModalInstagram.css';
import './chatModalWhatsApp.css';
import AddFaqFromHistoryModal from './AddFaqFromHistoryModal';
import NoKnowledgeModal from './NoKnowledgeModal';
import LiveAgentInfoModal from './LiveAgentInfoModal';
import ProductCarousel from './ProductCarousel';

// Typewriter for a status step's `detail` (the agent's composed search query):
// reveals the text a few characters at a time, so the visitor watches their
// question being turned into a sharper research query. Top-level component
// (NOT inside ChatModal) so re-renders never remount it and restart the
// animation; it only re-types when the text itself changes.
const StatusDetail = ({ text }) => {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    if (!text) return undefined;
    const iv = setInterval(() => {
      setShown((n) => (n >= text.length ? n : n + 2));
    }, 24);
    return () => clearInterval(iv);
  }, [text]);
  if (!text) return null;
  return (
    <span className="chat-status-detail">
      {'“'}{text.slice(0, shown)}{'”'}
    </span>
  );
};

// showImprove: the "Not the perfect answer? / Improve this answer" CTA under a bot
// reply. ON by default (the portal's Playground and Studio test chat are where an
// owner tunes the agent). The ONBOARDING flows pass false: there the chat is a
// first-impression demo, the owner has no mental model of skills yet, and the
// Agent-Studio hand-off would drop them into a tuning surface mid-signup.
const ChatModal = ({ open, onClose, bot, currentMessageCount, messagePlanLimit, planName, openSubscriptionModalFromChat, embedded = false, onMessageCountChange, liveCtaAlways = false, onLiveCta, showImprove = true, appearance = 'default', displayName }) => {
  const { t, i18n } = useTranslation();
  const isInstagram = appearance === 'instagram';
  const isWhatsApp = appearance === 'whatsapp';
  // Messenger previews (Instagram, WhatsApp): the agent's name and avatar, no visitor icon, no live-chat button.
  const isMessenger = isInstagram || isWhatsApp;
  const instagramName = displayName?.trim() || t('chat_bot_icon_agent');
  const instagramAvatar = isWhatsApp
    ? <span className="chat-whatsapp-avatar" aria-hidden="true">{instagramName.charAt(0).toUpperCase()}</span>
    : <img className="chat-instagram-avatar" src="/brand/instagram/Instagram_Glyph_Gradient.png" alt="" aria-hidden="true" />;
  const [userInput, setUserInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [predefinedQuestions, setPredefinedQuestions] = useState([]);
  const [animateIndex, setAnimateIndex] = useState(0);
  const [userAsked, setUserAsked] = useState(false);
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const inputRef = useRef(null); // chat input textarea — for auto-focus
  const pendingUserScroll = useRef(false);
  const welcomeRef = useRef([]); // cached welcome message(s) for "Clear chat"
  const currentBotMessage = useRef('');
  const currentBotProducts = useRef(null); // product cards for the in-flight reply (SSE `products` event)
  const [isStreaming, setIsStreaming] = useState(false);
  // Perplexity-style activity timeline for the in-flight reply (SSE `status`
  // events, server-localized labels). Builds up step by step while the agent
  // works and collapses away the moment the answer starts streaming.
  const [statusSteps, setStatusSteps] = useState([]);
  const [statusCollapsing, setStatusCollapsing] = useState(false);
  const statusCollapseTimer = useRef(null);
  // Ref mirror of statusCollapsing for the SSE handlers (closures would see a
  // stale state value): while true, incoming answer text is BUFFERED, not
  // rendered — the roll-up animation owns the stage alone, then the buffered
  // text is flushed in one go. This sequencing (instead of overlap) is what
  // keeps the hand-off completely still.
  const statusCollapsingRef = useRef(false);
  // The timeline block element — measured at collapse start to pre-grow the
  // bottom spacer by exactly the block's height, so scrollHeight can never
  // SHRINK mid-transition (a shrinking scrollHeight makes the browser clamp
  // scrollTop frame by frame — the "whole chat trembles" bug).
  const statusBlockRef = useRef(null);
  // Smooth character-reveal of the answer. The SSE stream arrives in chunky
  // bursts and the fade-out buffers ~0.5s of text; dumping either at once
  // reads as a text SLAB appearing. Instead the buffer is revealed a few
  // characters per tick (adaptive catch-up), so the answer always TYPES from
  // its first word — during the buffered start and through bursty streaming.
  const revealTimerRef = useRef(null);
  const revealActiveRef = useRef(false);
  const displayedLenRef = useRef(0);
  const answerDoneRef = useRef(false);
  useEffect(() => () => {
    if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
    if (revealTimerRef.current) clearInterval(revealTimerRef.current);
  }, []);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [prefilledQuestion, setPrefilledQuestion] = useState('');
  const [prefilledAnswer, setPrefilledAnswer] = useState('');
  const [prefilledBotId, setPrefilledBotId] = useState(null);
  const [localMessageCount, setLocalMessageCount] = useState(currentMessageCount);
  // AI credits on (backend controller/credits): figures are credits, an answer costs its level's price
  const aiCredits = useAiCredits(bot?.id);
  const inCredits = creditsOn(aiCredits);
  const answerStep = useRef(1);  // read inside the send callbacks, so always the current price
  answerStep.current = inCredits ? Number(aiCredits.answer_price || 1) : 1;
  const [currentBot, setCurrentBot] = useState(bot);
  const [showNoKnowledgeModal, setShowNoKnowledgeModal] = useState(false);
  const noKnowledgeShownRef = useRef(new Set());
  const navigate = useNavigate();

  // Usage refresh: testing the agent here consumes real AI responses against
  // the plan. Each time a response lands we broadcast `ub:usage-updated` so the
  // rest of the app (sidebar "AI responses" footer, Overview activity chart +
  // usage tile) re-pulls its figures live, without a manual refresh. Firing
  // per-response (rather than on close) means it also works for the always-open
  // embedded playground on the Overview page, which never unmounts while in use.
  const lastBroadcastCountRef = useRef(currentMessageCount);
  const botIdRef = useRef(bot?.id);
  useEffect(() => { botIdRef.current = bot?.id; }, [bot]);
  useEffect(() => {
    if (localMessageCount > lastBroadcastCountRef.current) {
      lastBroadcastCountRef.current = localMessageCount;
      window.dispatchEvent(
        new CustomEvent('ub:usage-updated', {
          detail: { botId: botIdRef.current, count: localMessageCount },
        })
      );
    }
  }, [localMessageCount]);

  // ─── Live-agent "Talk to a human" button ────────────────────────────────
  // The button is shown for parity with what real visitors see, but the
  // actual hand-off flow is intentionally NOT wired up inside the test
  // modal — see LiveAgentInfoModal for the rationale. We only need the
  // owner-facing visibility settings + the live availability flag here.
  const [liveSettings, setLiveSettings] = useState({
    show_request_button: false,
    request_button_text: '',
  });
  const [agentAvailable, setAgentAvailable] = useState(false);
  const [showLiveAgentInfoModal, setShowLiveAgentInfoModal] = useState(false);

  // Pre-chat form states
  const [requirePreChat, setRequirePreChat] = useState(false);
  const [preChatFields, setPreChatFields] = useState([]);
  const [preChatFormData, setPreChatFormData] = useState({});
  const [preChatCompleted, setPreChatCompleted] = useState(false);
  const [preChatSubmitting, setPreChatSubmitting] = useState(false);
  const [preChatAnyLanguage, setPreChatAnyLanguage] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  const [voiceState, setVoiceState] = useState('idle');
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const ttsAudioRef = useRef(null);
  const ttsAudioContextRef = useRef(null); // Dedicated audio context for TTS playback (iOS fix)
  const vadFrameIdRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const silenceStartTimeRef = useRef(null);
  const voiceStoppedByUserRef = useRef(false); // Track if user explicitly closed voice

  const VAD_THRESHOLD = 0.025; // Slightly higher threshold for clearer detection
  const MIN_SPEECH_MS = 400; // Require 400ms of speech
  const SILENCE_MS = 1000; // Wait 1s of silence before stopping

  const [audioLevel, setAudioLevel] = useState(0);
  const [processingPulse, setProcessingPulse] = useState(0); // For syncing visual to sound

  // Processing sound settings
  const PROCESSING_SOUND_VOLUME = 0.25; // 0.0 to 1.0
  const PROCESSING_SOUND_SPEED = 320; // milliseconds between pulses (higher = slower)

  // Processing sound refs
  const processingAudioContextRef = useRef(null);
  const processingOscillatorRef = useRef(null);
  const processingGainRef = useRef(null);
  const processingIntervalRef = useRef(null);

  // Start processing sound - Neural Pulse Even (deep processing pulses)
  const startProcessingSound = useCallback(() => {
    try {
      // Clean up any existing
      stopProcessingSound();

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      processingAudioContextRef.current = audioCtx;

      const gainNode = audioCtx.createGain();
      gainNode.connect(audioCtx.destination);
      processingGainRef.current = gainNode;

      const baseFreq = 140;
      let step = 0;
      const pattern = [0, 20, 40, 20, 0, -20, -40, -20];

      processingIntervalRef.current = setInterval(() => {
        if (!processingGainRef.current || !processingAudioContextRef.current) return;
        const ctx = processingAudioContextRef.current;
        const gain = processingGainRef.current;

        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = baseFreq + pattern[step % pattern.length];
        osc.connect(gain);

        // Volume setting
        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setValueAtTime(PROCESSING_SOUND_VOLUME, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);

        osc.start();
        osc.stop(ctx.currentTime + 0.14);

        // Update pulse state for visual sync (0-7 cycles through pattern)
        setProcessingPulse(step % pattern.length);
        step++;
      }, PROCESSING_SOUND_SPEED);

    } catch (e) {
      console.warn('Could not start processing sound:', e);
    }
  }, []);

  const stopProcessingSound = useCallback(() => {
    if (processingIntervalRef.current) {
      clearInterval(processingIntervalRef.current);
      processingIntervalRef.current = null;
    }
    if (processingOscillatorRef.current) {
      try {
        processingOscillatorRef.current.stop();
      } catch (e) {}
      processingOscillatorRef.current = null;
    }
    // Fade out the gain smoothly before closing
    if (processingGainRef.current && processingAudioContextRef.current) {
      try {
        const ctx = processingAudioContextRef.current;
        const gain = processingGainRef.current;
        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        // Close context after fade completes
        setTimeout(() => {
          if (processingAudioContextRef.current) {
            try {
              processingAudioContextRef.current.close();
            } catch (e) {}
            processingAudioContextRef.current = null;
          }
        }, 150);
      } catch (e) {
        if (processingAudioContextRef.current) {
          try {
            processingAudioContextRef.current.close();
          } catch (e) {}
          processingAudioContextRef.current = null;
        }
      }
      processingGainRef.current = null;
    } else if (processingAudioContextRef.current) {
      try {
        processingAudioContextRef.current.close();
      } catch (e) {}
      processingAudioContextRef.current = null;
    }
  }, []);

  // Start/stop processing sound based on voice state
  useEffect(() => {
    if (voiceState === 'thinking') {
      startProcessingSound();
    } else {
      stopProcessingSound();
    }
    return () => stopProcessingSound();
  }, [voiceState, startProcessingSound, stopProcessingSound]);

  // Update currentBot when bot prop changes
  useEffect(() => {
    if (bot) {
      setCurrentBot(bot);
    }
  }, [bot]);

  const openFaqModal = (question, answer, botId) => {
    setPrefilledQuestion(question || '');
    setPrefilledAnswer(answer || '');
    setPrefilledBotId(botId);
    setFaqModalOpen(true);
  };

  // "Improve this answer": hand the flagged reply to the Agent-Studio builder.
  // The question and answer become a prefilled builder message (the Studio
  // composer reads its draft from localStorage), then the Studio opens on Build.
  const openInStudio = (question, answer) => {
    try {
      localStorage.setItem(`agentStudio.draft.${bot.id}`, t('agent_studio_fix_draft', { question: question || '', answer: answer || '' }));
      localStorage.setItem(`agentStudio.mode.${bot.id}`, 'build');
      // the Studio opens for the selected bot (SelectedBotContext reads these on load)
      localStorage.setItem('selectedBotId', bot.id);
      localStorage.setItem('selectedBotIds', JSON.stringify([bot.id]));
    } catch (_) { /* storage unavailable: the Studio still opens */ }
    window.location.assign('/agent-studio/skills');
  };

  useEffect(() => {
    if (!document.getElementById('chat-modal-predefined-jump-style')) {
      const style = document.createElement('style');
      style.id = 'chat-modal-predefined-jump-style';
      style.textContent = `
        @keyframes jump {
          0%   { transform: translateY(0); }
          20%  { transform: translateY(-10px); }
          40%  { transform: translateY(4px); }
          60%  { transform: translateY(-6px); }
          80%  { transform: translateY(2px); }
          100% { transform: translateY(0); }
        }

        .predefined-question {
          border: 1px solid #e8e8e8;
          transition: border-color 0.4s ease, background-color 0.25s, transform 0.2s;
        }

        .predefined-question.jump {
          animation: jump 0.9s ease;
          border-color: #6d6d6dff !important;
        }

        /* Hovering a chip holds it still: the attention-grabbing jump is for
           chips the visitor has NOT engaged with, and a moving target is
           annoying to click. Dropping the animation (rather than pausing it)
           returns the chip to its resting position — and because transform
           carries a transition above, it settles smoothly instead of
           snapping. Only the hovered chip is affected; the rotation keeps
           running for the others. */
        .predefined-question.jump:hover {
          animation: none;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    // Embedded (inline) mode lives inside the page flow, so it must NOT lock
    // body scroll the way the fullscreen modal does.
    if (embedded) return undefined;
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, embedded]);

  // Surface the running message count to the parent (the Overview playground
  // uses this to tick its "AI responses" figure up live as you chat).
  useEffect(() => {
    onMessageCountChange?.(localMessageCount);
  }, [localMessageCount, onMessageCountChange]);

  const stopVoice = useCallback(() => {
    console.log('🛑 stopVoice called - user closing voice mode');
    // Mark that user explicitly stopped voice
    voiceStoppedByUserRef.current = true;

    // Stop processing sound
    stopProcessingSound();

    // Abort any ongoing fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (vadFrameIdRef.current) {
      cancelAnimationFrame(vadFrameIdRef.current);
      vadFrameIdRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    // Stop TTS audio - handle both Web Audio API and HTML Audio element
    if (ttsAudioRef.current) {
      if (ttsAudioRef.current.source) {
        // Web Audio API source
        try {
          ttsAudioRef.current.source.stop();
        } catch (e) {}
      } else if (ttsAudioRef.current.pause) {
        // HTML Audio element (fallback)
        ttsAudioRef.current.pause();
      }
      ttsAudioRef.current = null;
    }
    // Close TTS audio context
    if (ttsAudioContextRef.current) {
      ttsAudioContextRef.current.close().catch(() => {});
      ttsAudioContextRef.current = null;
    }
    // Clear audio queue for streaming TTS
    if (audioQueueRef.current) {
      audioQueueRef.current = [];
    }
    if (isPlayingRef.current !== undefined) {
      isPlayingRef.current = false;
    }
    streamDoneRef.current = false;
    audioChunksRef.current = [];
    setAudioLevel(0);
    setVoiceState('idle');
  }, [stopProcessingSound]);

  useEffect(() => {
    if (!open) {
      setUserInput('');
      setMessages([]);
      setLoading(false);
      setSessionId('');
      setPredefinedQuestions([]);
      setAnimateIndex(0);
      setUserAsked(false);
      currentBotMessage.current = '';
      currentBotProducts.current = null;
      setIsStreaming(false);
      setConfigLoading(true);
      stopVoice();
      // Reset live-agent button visibility so it doesn't flash when the
      // modal is reopened with a different bot.
      setLiveSettings({ show_request_button: false, request_button_text: '' });
      setAgentAvailable(false);
      setShowLiveAgentInfoModal(false);
    }
  }, [open, stopVoice]);

  // Fetch the live-chat settings + current agent availability whenever the
  // modal opens for a new bot, then re-poll availability every 30s while
  // open. This drives the visibility of the "Talk to a human" button only
  // \u2014 there is no WebSocket / heartbeat in the test modal.
  useEffect(() => {
    if (!open || !bot?.id) return undefined;
    let cancelled = false;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    const botId = bot.id;

    const loadSettings = async () => {
      try {
        const res = await fetch(`${apiBase}live_chat_settings_public/${botId}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setLiveSettings({
            show_request_button: !!data.show_request_button,
            request_button_text: data.request_button_text || '',
          });
        }
      } catch {
        /* non-fatal \u2014 button stays hidden */
      }
    };

    const loadAvailability = async () => {
      try {
        const res = await fetch(`${apiBase}live/agent_available/${botId}`, {
          cache: 'no-store',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAgentAvailable(!!data.available);
      } catch {
        if (!cancelled) setAgentAvailable(false);
      }
    };

    loadSettings();
    loadAvailability();
    const intervalId = setInterval(loadAvailability, 30000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [open, bot?.id]);

  // When the owner toggles their live-chat availability elsewhere in the portal
  // (the Overview/Live availability card fires `ub:agent-availability-changed`),
  // re-check availability immediately instead of waiting for the 30s poll, so
  // the "Talk to a human" button appears/disappears right away. We re-fetch
  // (authoritative) rather than trust the event, so it stays correct when a
  // teammate is still online after this agent goes offline.
  useEffect(() => {
    if (!open || !bot?.id) return undefined;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    const botId = bot.id;
    const onAvailabilityChanged = (e) => {
      if (e?.detail?.botId && String(e.detail.botId) !== String(botId)) return;
      // Going online → available for sure; reflect instantly (no round-trip).
      if (e?.detail?.online === true) setAgentAvailable(true);
      // Reconcile against the server: covers going offline (and the case where
      // a teammate is still online, so availability must stay true).
      (async () => {
        try {
          const res = await fetch(`${apiBase}live/agent_available/${botId}`, { cache: 'no-store' });
          if (!res.ok) return;
          const data = await res.json();
          setAgentAvailable(!!data.available);
        } catch {
          /* keep current state on error */
        }
      })();
    };
    window.addEventListener('ub:agent-availability-changed', onAvailabilityChanged);
    return () => window.removeEventListener('ub:agent-availability-changed', onAvailabilityChanged);
  }, [open, bot?.id]);

  const computeRMS = useCallback((analyser) => {
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const val = (data[i] - 128) / 128;
      sum += val * val;
    }
    return Math.sqrt(sum / data.length);
  }, []);

  // Audio queue for streaming TTS playback
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const currentTranscriptRef = useRef('');
  const botResponseRef = useRef('');
  const streamDoneRef = useRef(false); // Track if backend stream finished
  const startListeningRef = useRef(null); // Ref to avoid circular dependency
  const abortControllerRef = useRef(null); // For cancelling fetch on barge-in
  const consecutiveFailuresRef = useRef(0); // Track consecutive empty transcripts to prevent infinite loops
  const MAX_CONSECUTIVE_FAILURES = 3; // Stop voice mode after this many failures

  const playNextInQueue = useCallback(() => {
    console.log('🎵 playNextInQueue called, queue length:', audioQueueRef.current.length, 'streamDone:', streamDoneRef.current);

    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;

      // Only go back to listening if stream is fully done AND user hasn't explicitly stopped
      if (streamDoneRef.current && !voiceStoppedByUserRef.current) {
        console.log('🔄 Queue empty & stream done, transitioning...');
        // Add bot message now that all audio has played
        if (botResponseRef.current) {
          setMessages(m => [...m, { text: botResponseRef.current, from: 'bot' }]);
          setLocalMessageCount(prev => prev + answerStep.current);
          botResponseRef.current = '';
        }
        // Go back to listening mode for continuous conversation
        if (startListeningRef.current) {
          console.log('🎤 Restarting listening...');
          startListeningRef.current();
        }
      } else if (voiceStoppedByUserRef.current) {
        console.log('🛑 User stopped voice mode, not restarting listening');
      }
      return;
    }

    isPlayingRef.current = true;
    setVoiceState('speaking');

    const nextAudio = audioQueueRef.current.shift();
    console.log('🔊 Playing audio chunk', nextAudio.index);

    // 🔑 iOS FIX: Use Web Audio API instead of Audio element for better iOS compatibility
    const playWithWebAudio = async () => {
      try {
        // Reuse or create audio context for TTS
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
          ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const audioContext = ttsAudioContextRef.current;

        // Resume if suspended (iOS suspends AudioContext by default)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Decode base64 to ArrayBuffer
        const binaryString = atob(nextAudio.audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Decode audio data
        const audioBuffer = await audioContext.decodeAudioData(bytes.buffer.slice(0));

        // Create and play source
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        source.onended = () => {
          console.log('✅ Audio chunk ended (Web Audio)');
          ttsAudioRef.current = null;
          playNextInQueue();
        };

        source.start(0);

        // Store reference for stopping
        ttsAudioRef.current = { source, context: audioContext };

      } catch (e) {
        console.error('Web Audio playback failed, falling back to Audio element:', e);
        // Fallback to original HTML Audio method
        const audioSrc = `data:audio/mp3;base64,${nextAudio.audio}`;
        const audio = new Audio(audioSrc);
        ttsAudioRef.current = audio;

        audio.onended = () => {
          console.log('✅ Audio chunk ended (fallback)');
          ttsAudioRef.current = null;
          playNextInQueue();
        };

        audio.onerror = (err) => {
          console.error('❌ Audio error (fallback):', err);
          ttsAudioRef.current = null;
          playNextInQueue();
        };

        audio.play().catch((err) => {
          console.error('❌ Audio play failed (fallback):', err);
          ttsAudioRef.current = null;
          playNextInQueue();
        });
      }
    };

    playWithWebAudio();
  }, []);

  // Helper to detect supported audio MIME type for MediaRecorder (iOS Safari doesn't support webm)
  const getSupportedMimeType = useCallback(() => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav',
      ''  // Empty string = browser default
    ];

    for (const type of types) {
      if (type === '' || MediaRecorder.isTypeSupported(type)) {
        console.log(`🎤 Using MIME type: ${type || 'browser default'}`);
        return type;
      }
    }
    return '';
  }, []);

  const sendVoiceToBackend = useCallback(async (audioBlob) => {
    if (!userAsked) setUserAsked(true);
    setVoiceState('thinking');
    setLoading(true);

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Reset state for new request
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    currentTranscriptRef.current = '';
    botResponseRef.current = '';
    streamDoneRef.current = false;

    // Get file extension from blob type for proper backend handling
    const mimeToExt = {
      'audio/webm': 'webm',
      'audio/webm;codecs=opus': 'webm',
      'audio/mp4': 'mp4',
      'audio/ogg': 'ogg',
      'audio/ogg;codecs=opus': 'ogg',
      'audio/wav': 'wav',
    };
    const ext = mimeToExt[audioBlob.type] || 'webm';

    const formData = new FormData();
    formData.append('audio', audioBlob, `recording.${ext}`);
    formData.append('session_id', sessionId);
    formData.append('bot_id', bot.id);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}voice_message`, {
        method: 'POST',
        body: formData,
        signal: abortController.signal
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        // Check if aborted
        if (abortController.signal.aborted) {
          console.log('🛑 Request aborted, stopping stream processing');
          reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newlines
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const event of events) {
          // Skip if aborted mid-processing
          if (abortController.signal.aborted) break;

          if (!event.trim()) continue;

          let eventType = '';
          let eventData = '';

          for (const line of event.split('\n')) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);
            console.log(`📨 SSE Event: ${eventType}`, data.audio ? '(audio data)' : data);

            if (eventType === 'transcript' && data.text) {
              currentTranscriptRef.current = data.text;
              setMessages(m => [...m, { text: data.text, from: 'user' }]);
              // Successful transcript - reset failure counter
              consecutiveFailuresRef.current = 0;
            }
            else if (eventType === 'text' && data.chunk) {
              // Accumulate text chunks for partial response preservation
              botResponseRef.current += data.chunk;
            }
            else if (eventType === 'audio' && data.audio) {
              console.log(`🔊 Audio chunk ${data.index} received, queue length: ${audioQueueRef.current.length}`);
              // Add to queue
              audioQueueRef.current.push(data);

              // Start playing if not already
              if (!isPlayingRef.current) {
                console.log('▶️ Starting playback');
                playNextInQueue();
              }
            }
            else if (eventType === 'done') {
              console.log('✅ Stream done, queue length:', audioQueueRef.current.length, 'isPlaying:', isPlayingRef.current);
              botResponseRef.current = data.full_response || '';
              streamDoneRef.current = true;

              // If empty transcript (e.g., audio too short), handle with failure counter
              if (data.reason === 'empty_transcript' || (!data.full_response && audioQueueRef.current.length === 0)) {
                consecutiveFailuresRef.current++;
                console.log(`📭 Empty response (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);
                setLoading(false);

                // Stop if too many consecutive failures
                if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                  console.log('❌ Too many consecutive failures, stopping voice mode');
                  consecutiveFailuresRef.current = 0;
                  // Force fresh stream next time by clearing the ref
                  if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop());
                    mediaStreamRef.current = null;
                  }
                  if (audioContextRef.current) {
                    audioContextRef.current.close().catch(() => {});
                    audioContextRef.current = null;
                  }
                  setVoiceState('idle');
                  return;
                }

                // Restart with a small delay and force fresh stream
                if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                  // Clear old stream to force fresh one
                  if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop());
                    mediaStreamRef.current = null;
                  }
                  if (audioContextRef.current) {
                    audioContextRef.current.close().catch(() => {});
                    audioContextRef.current = null;
                  }
                  // Small delay before restart
                  setTimeout(() => {
                    if (!voiceStoppedByUserRef.current) {
                      startListeningRef.current();
                    }
                  }, 500);
                }
                return;
              }

              // Success - reset failure counter
              consecutiveFailuresRef.current = 0;

              // If no audio or already done, handle immediately
              if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                if (data.full_response) {
                  setMessages(m => [...m, { text: data.full_response, from: 'bot' }]);
                  setLocalMessageCount(prev => prev + answerStep.current);
                }
                botResponseRef.current = '';
                if (startListeningRef.current) {
                  startListeningRef.current();
                }
              }
            }
            else if (eventType === 'error') {
              console.error('Voice stream error:', data.message);
              setMessages(m => [...m, { text: t('chat_voice_failed'), from: 'bot' }]);
              setVoiceState('idle');
            }
          } catch (e) {
            console.warn('Failed to parse SSE data:', eventData, e);
          }
        }
      }
    } catch (err) {
      // Don't show error for aborted requests (barge-in)
      if (err.name === 'AbortError') {
        console.log('🛑 Request was aborted (barge-in)');
        // Show any accumulated text as bot message
        if (botResponseRef.current && botResponseRef.current.trim()) {
          const partialResponse = botResponseRef.current;
          setMessages(m => [...m, { text: partialResponse, from: 'bot' }]);
          setLocalMessageCount(prev => prev + answerStep.current);
          console.log('📝 Saved partial response:', partialResponse.substring(0, 50) + '...');
        }
        botResponseRef.current = '';
        return;
      }
      console.error('Voice request failed:', err);

      // Track failure
      consecutiveFailuresRef.current++;
      console.log(`⚠️ Error occurred (failure ${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES})`);

      // Stop if too many consecutive failures
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        console.log('❌ Too many consecutive failures, stopping voice mode');
        consecutiveFailuresRef.current = 0;
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setVoiceState('idle');
        return;
      }

      // Restart with fresh stream after small delay
      if (startListeningRef.current && !voiceStoppedByUserRef.current) {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }
        setTimeout(() => {
          if (!voiceStoppedByUserRef.current) {
            startListeningRef.current();
          }
        }, 500);
      } else {
        setVoiceState('idle');
      }
    } finally {
      setLoading(false);
      // Clear abort controller reference when done
      if (abortControllerRef.current?.signal.aborted === false) {
        abortControllerRef.current = null;
      }
    }
  }, [sessionId, bot.id, userAsked, playNextInQueue, t]);

  const startListening = useCallback(async () => {
    try {
      // Note: iOS audio unlock is done in toggleVoice during user gesture
      // Here we just handle the microphone stream setup

      // Reuse existing stream/context if available, otherwise create new
      let stream = mediaStreamRef.current;
      let audioCtx = audioContextRef.current;
      let analyser = analyserRef.current;

      // Only create new stream if we don't have one or it's not active
      if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
        console.log('🎤 Creating new media stream');
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;

        // Need new audio context for new stream
        if (audioCtx) {
          audioCtx.close().catch(() => {});
        }
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;
      } else {
        console.log('🎤 Reusing existing media stream');
        // Resume audio context if it was suspended
        if (audioCtx && audioCtx.state === 'suspended') {
          await audioCtx.resume();
        }
      }

      // Get supported MIME type for this browser/device (iOS doesn't support webm)
      const mimeType = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        // Don't send audio if user explicitly closed voice mode
        if (voiceStoppedByUserRef.current) {
          console.log('🛑 Recorder stopped but user closed voice mode, discarding audio');
          audioChunksRef.current = [];
          return;
        }

        if (audioChunksRef.current.length > 0) {
          // Use the actual MIME type from the recorder (important for iOS which uses mp4)
          const actualMimeType = recorder.mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
          audioChunksRef.current = [];

          // Validate audio before sending - check size and speech duration
          const speechDuration = speechStartTimeRef.current
            ? Date.now() - speechStartTimeRef.current
            : 0;

          // Skip if audio is too short (< 500ms of speech) or blob too small (< 3KB)
          // Whisper needs at least ~100ms of valid audio to process
          if (blob.size < 3000 || speechDuration < 500) {
            console.log(`⚠️ Audio rejected: size=${blob.size}, speechDuration=${speechDuration}ms`);
            // Go back to listening without sending
            if (startListeningRef.current && !voiceStoppedByUserRef.current) {
              startListeningRef.current();
            }
            return;
          }

          sendVoiceToBackend(blob);
        }
      };

      recorder.start(100);
      setVoiceState('listening');
      speechStartTimeRef.current = null;
      silenceStartTimeRef.current = null;

      const vadLoop = () => {
        if (!analyserRef.current) return;

        const rms = computeRMS(analyserRef.current);
        setAudioLevel(Math.min(rms * 10, 1));
        const now = Date.now();

        if (rms > VAD_THRESHOLD) {
          silenceStartTimeRef.current = null;
          if (!speechStartTimeRef.current) {
            speechStartTimeRef.current = now;
          }
        } else {
          if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
            if (!silenceStartTimeRef.current) {
              silenceStartTimeRef.current = now;
            } else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
              return;
            }
          }
        }

        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
      };

      vadFrameIdRef.current = requestAnimationFrame(vadLoop);

    } catch (err) {
      console.error('Microphone access denied:', err);
      setVoiceState('idle');
    }
  }, [computeRMS, sendVoiceToBackend]);

  // Keep ref updated for playNextInQueue to call
  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  // VAD loop for listening state - runs when we have a recorder but may not have gone through startListening
  // (e.g., after barge-in when recorder was adopted)
  useEffect(() => {
    if (voiceState !== 'listening' || !analyserRef.current || !mediaRecorderRef.current) {
      return;
    }

    // Check if recorder is actually recording
    if (mediaRecorderRef.current.state !== 'recording') {
      return;
    }

    console.log('🎧 Starting VAD loop for listening state');

    const vadLoop = () => {
      if (!analyserRef.current || voiceState !== 'listening') return;

      const rms = computeRMS(analyserRef.current);
      setAudioLevel(Math.min(rms * 10, 1));
      const now = Date.now();

      if (rms > VAD_THRESHOLD) {
        silenceStartTimeRef.current = null;
        if (!speechStartTimeRef.current) {
          speechStartTimeRef.current = now;
        }
      } else {
        if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
          if (!silenceStartTimeRef.current) {
            silenceStartTimeRef.current = now;
          } else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
            console.log('🔇 Silence detected, stopping recorder');
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
            return;
          }
        }
      }

      vadFrameIdRef.current = requestAnimationFrame(vadLoop);
    };

    vadFrameIdRef.current = requestAnimationFrame(vadLoop);

    return () => {
      if (vadFrameIdRef.current) {
        cancelAnimationFrame(vadFrameIdRef.current);
        vadFrameIdRef.current = null;
      }
    };
  }, [voiceState, computeRMS]);

  // Stop current playback and return to listening mode
  const stopAndSpeakAgain = useCallback(() => {
    console.log('🔄 Stop and speak again - returning to listening mode');

    // Abort any ongoing fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Stop current audio - handle both Web Audio API and HTML Audio element
    if (ttsAudioRef.current) {
      if (ttsAudioRef.current.source) {
        // Web Audio API source
        try {
          ttsAudioRef.current.source.stop();
        } catch (e) {}
      } else if (ttsAudioRef.current.pause) {
        // HTML Audio element (fallback)
        ttsAudioRef.current.pause();
      }
      ttsAudioRef.current = null;
    }

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    streamDoneRef.current = false;

    // Reset speech detection timers
    speechStartTimeRef.current = null;
    silenceStartTimeRef.current = null;

    // Start listening again
    startListening();
  }, [startListening]);

  const toggleVoice = useCallback(async () => {
    if (voiceState === 'idle') {
      // Reset the stopped-by-user flag when starting fresh
      voiceStoppedByUserRef.current = false;
      // Show overlay immediately with initializing state
      setVoiceState('initializing');

      // 🔑 iOS FIX: ALL of this must happen in direct user gesture call stack
      // iOS requires getUserMedia AND audio unlock to be in the synchronous tap handler

      try {
        // 1. Unlock audio playback with silent audio (for TTS later)
        const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
        silentAudio.volume = 0;
        silentAudio.play().then(() => silentAudio.pause()).catch(() => {});
        console.log('🔓 Audio playback unlock initiated');

        // 2. Pre-initialize TTS AudioContext during user gesture
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
          ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          console.log('🔓 TTS AudioContext created during user gesture');
        }
        if (ttsAudioContextRef.current.state === 'suspended') {
          ttsAudioContextRef.current.resume().catch(() => {});
        }

        // 3. 🔑 CRITICAL: Request microphone permission IMMEDIATELY during user gesture
        // This CANNOT be in setTimeout or after await on iOS
        console.log('🎤 Requesting microphone permission during user gesture...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone permission granted!');
        mediaStreamRef.current = stream;

        // 4. Now set up audio context and recorder (can be after await since we have the stream)
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
        }
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyserRef.current = analyser;

        // 5. Start the recorder and listening
        startListening();

      } catch (err) {
        console.error('Failed to initialize voice:', err);
        setVoiceState('idle');
      }
    } else {
      stopVoice();
    }
  }, [voiceState, startListening, stopVoice]);

  useEffect(() => {
    if (open) {
      const generateSessionId = () => `portal-${Date.now()}-${Math.floor(Math.random() * 10000) + 1}`;
      setSessionId(generateSessionId());

      // Reset pre-chat state when modal opens
      setPreChatCompleted(false);
      setPreChatFormData({});

      const fetchWelcomeMessage = async () => {
        setConfigLoading(true);
        const startTime = Date.now();
        try {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}widget_configuration/${bot.id}`);
          const data = await res.json();
          const welcomeMessages = Array.isArray(data.welcome_message)
            ? data.welcome_message
            : data.welcome_message
            ? [data.welcome_message]
            : [t('chat_welcome_fallback')];
          const welcomeMsgs = welcomeMessages.map((msg) => ({ text: msg, from: 'bot' }));
          welcomeRef.current = welcomeMsgs;
          setMessages(welcomeMsgs);

          let pQuestions = data.predefined_questions ?? [];
          try {
            if (typeof pQuestions === 'string') pQuestions = JSON.parse(pQuestions);
          } catch {}
          const hasRealQuestions =
            Array.isArray(pQuestions) &&
            (pQuestions.length > 1 || (pQuestions.length === 1 && pQuestions[0].trim() !== ''));
          setPredefinedQuestions(hasRealQuestions ? pQuestions : []);

          // Check for pre-chat requirement
          const requiresPreChat = data.require_pre_chat === true;
          let requiredFieldIds = [];
          try {
            requiredFieldIds = typeof data.pre_chat_required_fields === 'string'
              ? JSON.parse(data.pre_chat_required_fields)
              : data.pre_chat_required_fields || [];
          } catch {
            requiredFieldIds = [];
          }

          if (requiresPreChat && requiredFieldIds.length > 0) {
            // Fetch warm lead parameters to get field details
            try {
              const warmLeadRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${bot.id}`);
              const warmLeadData = await warmLeadRes.json();
              const allParams = warmLeadData.bot_function_parameters || [];

              // Filter to only the required fields
              const requiredFields = allParams.filter(p => requiredFieldIds.includes(p.id));

              if (requiredFields.length > 0) {
                setRequirePreChat(true);
                setPreChatFields(requiredFields);
                setPreChatAnyLanguage(warmLeadData.any_language_note === true);
                // Initialize form data
                const initialFormData = {};
                requiredFields.forEach(field => {
                  initialFormData[field.name] = '';
                });
                setPreChatFormData(initialFormData);
              } else {
                setRequirePreChat(false);
              }
            } catch (err) {
              console.error('Error fetching warm lead parameters:', err);
              setRequirePreChat(false);
            }
          } else {
            setRequirePreChat(false);
          }
        } catch {
          setMessages([{ text: t('chat_welcome_load_failed'), from: 'bot' }]);
        } finally {
          // Ensure spinner shows for at least 500ms
          const elapsed = Date.now() - startTime;
          const minDelay = 500;
          if (elapsed < minDelay) {
            setTimeout(() => setConfigLoading(false), minDelay - elapsed);
          } else {
            setConfigLoading(false);
          }
        }
      };
      fetchWelcomeMessage();
    }
  }, [open, bot.id, t]);

  useEffect(() => {
    if (!userAsked && predefinedQuestions.length > 0) {
      const id = setInterval(() => {
        setAnimateIndex((i) => (i + 1) % predefinedQuestions.length);
      }, 2000);
      return () => clearInterval(id);
    }
  }, [userAsked, predefinedQuestions]);

  // When user sends a message, scroll so their message is at the top of the viewport.
  // A spacer div at the bottom of the scroll container expands so the last user message
  // can actually be scrolled all the way to the top (otherwise scrollTop maxes out).
  const scrollAttemptsLeft = useRef(0);
  const bottomSpacerRef = useRef(null);
  const spacerActiveRef = useRef(false); // true while spacer is keeping user msg at top
  const programmaticScrollRef = useRef(false); // true while we are setting scrollTop programmatically

  // Messenger previews (Instagram, WhatsApp) pin the sent bubble a little below the top edge. That room may
  // never be wider than the gap above the bubble, or the bottom of the previous answer peeks in above it.
  const messengerInset = useCallback((container, msg) => {
    if (!isMessenger) return 0;
    const prev = msg?.previousElementSibling;
    const gap = prev ? msg.offsetTop - (prev.offsetTop + prev.offsetHeight) : 16;
    // 2px less than the gap: the bubbles' drop shadow must stay out of view too.
    return Math.max(0, Math.min(16, gap - 2)) + (parseFloat(getComputedStyle(container).paddingBottom) || 0);
  }, [isMessenger]);

  // Helper: recalculate spacer so user message stays at top.
  // Derives needed spacer from current measurements WITHOUT collapsing it
  // (collapsing causes the browser to clamp scrollTop → visual jump).
  const recalcSpacer = useCallback(() => {
    if (!bottomSpacerRef.current || !messageContainerRef.current) return 0;
    const container = messageContainerRef.current;
    const allUserMsgs = container.querySelectorAll('.user-message');
    const lastUserMsg = allUserMsgs[allUserMsgs.length - 1];
    if (!lastUserMsg) { bottomSpacerRef.current.style.height = '0px'; spacerActiveRef.current = false; return 0; }

    const spacer = bottomSpacerRef.current;

    const containerH = container.clientHeight;
    const msgOffsetTop = lastUserMsg.offsetTop;
    // Use spacer's offsetTop to get actual content height —
    // scrollHeight is clamped to clientHeight when content is short,
    // which would break calculations for the first message.
    const naturalContentH = spacer.offsetTop;
    const contentBelowUserMsg = naturalContentH - msgOffsetTop;

    const inset = messengerInset(container, lastUserMsg);
    const neededSpacer = Math.max(0, containerH - contentBelowUserMsg - inset);
    spacer.style.height = neededSpacer + 'px';
    spacerActiveRef.current = neededSpacer > 0;
    // Don't touch scrollTop — spacer is below messages, shrinking it
    // doesn't move any content above, so the view stays stable.
    return neededSpacer;
  }, [messengerInset]);

  // Initial scroll positioning — ONLY runs right after user hits send.
  // Sizes the spacer so that when scrolled to absolute bottom, the user
  // message sits at the top with 8px breathing room. Then scrolls to
  // absolute bottom. This means user can scroll UP (to see older messages)
  // but NOT down — the scrollbar is already fully at the bottom.
  useLayoutEffect(() => {
    let rafId;
    if (pendingUserScroll.current) {
      pendingUserScroll.current = false;
      scrollAttemptsLeft.current = isMessenger ? 1 : 6;
    }

    if (scrollAttemptsLeft.current > 0 && messageContainerRef.current) {
      scrollAttemptsLeft.current -= 1;

      const container = messageContainerRef.current;
      const allUserMsgs = container.querySelectorAll('.user-message');
      const target = allUserMsgs[allUserMsgs.length - 1];
      if (target) {
        const measureAndSizeSpacer = () => {
          if (!bottomSpacerRef.current || !messageContainerRef.current) return;
          const c = messageContainerRef.current;
          const containerH = c.clientHeight;

          // Collapse spacer, then use spacer's offsetTop to get true
          // content height (scrollHeight is clamped to clientHeight
          // when content is short — breaks first-message positioning).
          // Collapsing clamps scrollTop upward when the raw content is
          // shorter than the viewport — restore it after sizing, or the
          // smooth scroll below starts at the TOP of the history.
          const prevScrollTop = c.scrollTop;
          bottomSpacerRef.current.style.height = '0px';
          const naturalH = bottomSpacerRef.current.offsetTop;
          const msgOffsetTop = target.offsetTop;
          const contentBelowMsg = naturalH - msgOffsetTop;

          const inset = messengerInset(c, target);
          const spacerH = Math.max(0, containerH - contentBelowMsg - inset);
          bottomSpacerRef.current.style.height = spacerH + 'px';
          spacerActiveRef.current = spacerH > 0;
          c.scrollTop = prevScrollTop;
        };

        // 1) Size the spacer immediately (layout-phase, no paint yet).
        measureAndSizeSpacer();

        // 2) After paint, re-measure (layout may have shifted on first
        //    send when userAsked changes), then smooth-scroll to bottom.
        rafId = requestAnimationFrame(() => {
          measureAndSizeSpacer();
          if (!messageContainerRef.current) return;
          const c = messageContainerRef.current;
          programmaticScrollRef.current = true;
          c.scrollTo({ top: c.scrollHeight - c.clientHeight, behavior: 'smooth' });
        });
      }
    }
    return () => { if (rafId) cancelAnimationFrame(rafId); };
  }, [messages, loading, userAsked, isMessenger, messengerInset]);

  // Silently shrink spacer as bot response grows so the scrollbar doesn't
  // balloon. This NEVER touches scrollTop — it only adjusts the invisible
  // spacer below messages, which doesn't move any visible content.
  // statusSteps is a dependency ON PURPOSE: every row the activity timeline
  // adds is absorbed by the spacer in the SAME layout pass (net height under
  // the pinned user message stays constant), so the chat never jumps while
  // the timeline grows or rolls away.
  useLayoutEffect(() => {
    // Skip during the initial positioning phase (scrollAttemptsLeft handles that)
    if (scrollAttemptsLeft.current > 0) return;
    // While the timeline rolls away, collapseStatus() has pre-grown the spacer
    // by the block's exact height and OWNS the scroll geometry — a recalc from
    // mid-animation heights here would reintroduce the trembling.
    if (statusCollapsing) return;
    if (!spacerActiveRef.current) return;
    recalcSpacer();
  }, [messages, isStreaming, loading, statusSteps, statusCollapsing, recalcSpacer]);

  // Track whether the user can see the bottom of the chat (for scroll-down button)
  // Hide it while the spacer is actively keeping user message at top
  const updateScrollDownVisibility = useCallback(() => {
    const container = messageContainerRef.current;
    if (!container) return;
    // Don't show scroll-down during the initial positioning animation
    if (scrollAttemptsLeft.current > 0 || programmaticScrollRef.current) {
      setShowScrollDown(false);
      return;
    }
    // Nor during the timeline fade-out: the pre-grown spacer makes the
    // bottom-distance momentarily equal the block height, which would flash
    // the arrow for the 460ms hand-off.
    if (statusCollapsingRef.current) {
      setShowScrollDown(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 40;
    setShowScrollDown(!isAtBottom && scrollHeight > clientHeight);
  }, []);

  useEffect(() => {
    // statusSteps: the activity timeline can push content below the fold in
    // long chats before any message changes — the arrow must react to it too.
    updateScrollDownVisibility();
  }, [messages, loading, statusSteps, updateScrollDownVisibility]);

  const handleContainerScroll = useCallback(() => {
    // Ignore scroll events triggered by our own programmatic / smooth scroll.
    // Smooth scrolling fires many onScroll events; keep ignoring until we
    // arrive at the target (bottom). Clear the flag once we're there.
    if (programmaticScrollRef.current) {
      const c = messageContainerRef.current;
      if (c && c.scrollHeight - c.scrollTop - c.clientHeight < 2) {
        programmaticScrollRef.current = false;
      }
      return;
    }
    // If user scrolls manually, cancel any remaining auto-positioning attempts
    // so we never yank them back to the user message.
    if (scrollAttemptsLeft.current > 0) {
      scrollAttemptsLeft.current = 0;
    }
    updateScrollDownVisibility();
  }, [updateScrollDownVisibility]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Reset the conversation back to the welcome message. The message count is
  // intentionally NOT reset — those messages were really sent and still count
  // toward usage.
  const clearChat = () => {
    if (loading || isStreaming) return;
    setUserInput('');
    setUserAsked(false);
    setAnimateIndex(0);
    currentBotMessage.current = '';
    currentBotProducts.current = null;
    setIsStreaming(false);
    setLoading(false);
    setSessionId(`portal-${Date.now()}-${Math.floor(Math.random() * 10000) + 1}`);
    setMessages(
      welcomeRef.current.length
        ? welcomeRef.current
        : [{ text: t('chat_welcome_fallback'), from: 'bot' }]
    );
  };

  // Handle pre-chat form field change
  const handlePreChatFieldChange = (fieldName, value) => {
    setPreChatFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Check if all required pre-chat fields are filled
  const isPreChatFormValid = () => {
    return preChatFields.every(field =>
      preChatFormData[field.name] && preChatFormData[field.name].trim() !== ''
    );
  };

  // Submit pre-chat form and create lead
  const handlePreChatSubmit = async () => {
    if (!isPreChatFormValid()) return;

    setPreChatSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}leads/pre_chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bot_id: bot.id,
          session_id: sessionId,
          params: preChatFormData,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save contact details');
      }

      setPreChatCompleted(true);
    } catch (err) {
      console.error('Error submitting pre-chat form:', err);
      // Still allow chat even if lead creation fails
      setPreChatCompleted(true);
    } finally {
      setPreChatSubmitting(false);
    }
  };

  // sessionOverride: lets a caller re-ask a question in a fresh session in the
  // same tick (the sessionId state update hasn't landed yet).
  const sendMessage = async (messageOverride, sessionOverride) => {
    const text =
      typeof messageOverride === 'string'
        ? messageOverride
        : userInput.trim();
    if (!text) return;

    if (!userAsked) setUserAsked(true);
    setMessages((m) => [...m, { text, from: 'user' }]);
    pendingUserScroll.current = true;
    if (typeof messageOverride !== 'string') setUserInput('');

    setLoading(true);
    currentBotMessage.current = '';
    currentBotProducts.current = null;
    setIsStreaming(true);
    // Fresh reply -> fresh activity timeline (cancel a still-running collapse
    // or reveal from the previous reply so their timers can't touch this one).
    if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
    if (revealTimerRef.current) clearInterval(revealTimerRef.current);
    revealTimerRef.current = null;
    revealActiveRef.current = false;
    displayedLenRef.current = 0;
    answerDoneRef.current = false;
    setStatusSteps([]);
    setStatusCollapsing(false);
    statusCollapsingRef.current = false;

    const sid = typeof sessionOverride === 'string' && sessionOverride ? sessionOverride : sessionId;
    const url = `${process.env.REACT_APP_API_BASE_URL}chatbot_response?user_input=${encodeURIComponent(
      text
    )}&session_id=${sid}&bot_id=${bot.id}&language=english`;

    // Render exactly `text` as the (single) in-flight bot message. All paths
    // go through here — the reveal loop with a slice, finalization with the
    // full buffer — so there is one writer and no competing states.
    const renderBotText = (text) =>
      setMessages((msgs) => {
        const last = msgs[msgs.length - 1];
        const products = currentBotProducts.current || (last?.from === 'bot' ? last.products : null);
        if (last?.from === 'bot') {
          return [...msgs.slice(0, -1), { ...last, text, products }];
        }
        return [...msgs, { text, from: 'bot', products }];
      });

    // While the timeline fades out AND while the reveal loop is typing, SSE
    // chunks only fill the buffer (refs) — they never render directly. One
    // writer owns the message at any moment; that is the no-flicker contract.
    const upsertBotMessage = () => {
      if (statusCollapsingRef.current || revealActiveRef.current) return;
      renderBotText(currentBotMessage.current);
    };

    const stopReveal = () => {
      if (revealTimerRef.current) {
        clearInterval(revealTimerRef.current);
        revealTimerRef.current = null;
      }
      revealActiveRef.current = false;
    };

    const finish = () => {
      setLoading(false);
      setIsStreaming(false);
      // Hard cleanup for replies that end without streaming text (errors,
      // empty answers) — the collapse animation only runs on the happy path.
      if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
      stopReveal();
      statusCollapsingRef.current = false;
      setStatusSteps([]);
      setStatusCollapsing(false);
    };

    // Error messages for different error types
    const ERROR_MESSAGES = {
      rate_limit: t('chat_err_rate_limit'),
      timeout: t('chat_err_timeout'),
      internal_error: t('chat_err_internal'),
      validation_error: t('chat_err_validation'),
      bot_not_found: t('chat_err_bot_not_found'),
      default: t('chat_err_default')
    };

    return new Promise((resolve) => {
      const es = new EventSource(url);
      let firstChunk = true;
      let hasError = false;
      let finalized = false;

      const finalizeReply = () => {
        if (finalized) return;
        finalized = true;
        stopReveal();
        renderBotText(currentBotMessage.current);
        finish();
        setLocalMessageCount((prev) => prev + answerStep.current);
        resolve();
      };

      // Character-level reveal: every 30ms show a few more characters of the
      // buffer. The step adapts to the backlog (large right after the
      // fade-out, small once caught up with the live stream), so the answer
      // types smoothly from its FIRST word instead of slamming a text slab
      // in — and bursty SSE chunks are ironed out for the whole reply.
      const startReveal = () => {
        if (revealTimerRef.current) clearInterval(revealTimerRef.current);
        revealActiveRef.current = true;
        revealTimerRef.current = setInterval(() => {
          const full = currentBotMessage.current;
          if (displayedLenRef.current < full.length) {
            const backlog = full.length - displayedLenRef.current;
            displayedLenRef.current = Math.min(
              full.length,
              displayedLenRef.current + Math.max(1, Math.ceil(backlog / 25))
            );
            renderBotText(full.slice(0, displayedLenRef.current));
          } else if (answerDoneRef.current) {
            finalizeReply();
          }
        }, 30);
      };

      // The answer is starting to stream. Choreography — each part exists so
      // the hand-off is COMPLETELY still (no jitter, no avatar movement):
      // 1. Kill the leftover send-time positioning attempts. Their layout
      //    effect re-fires on setLoading(false), collapses the spacer to 0px
      //    to measure (chat jumps UP) and then smooth-scrolls back DOWN —
      //    that WAS the up-for-200ms-then-down flicker at answer start.
      // 2. Pre-grow the bottom spacer by the block's exact height so the
      //    fading bubble can never SHRINK scrollHeight (a shrinking
      //    scrollHeight clamps scrollTop frame by frame -> whole-chat
      //    trembling).
      // 3. The bubble fades out as ONE piece, then folds shut while already
      //    invisible; the avatar sits OUTSIDE it and does not move a pixel.
      // 4. In ONE batched commit the steps are removed and the first
      //    characters render — the answer message mounts identical avatar
      //    markup in the identical flow position (no blank frame), and the
      //    reveal loop types the rest from the first word.
      // Timer must outlast the CSS fade+fold (see chatModal.css
      // .chat-status-timeline.collapsing, ends at ~440ms).
      const collapseStatus = () => {
        scrollAttemptsLeft.current = 0;
        // Also retire the smooth-scroll-in-flight flag. The send-time smooth
        // scroll can land off-bottom when the growing timeline changed
        // scrollHeight mid-animation; its completion check (a scroll event
        // arriving at the bottom) then never fires and the STALE flag makes
        // updateScrollDownVisibility force-hide the scroll-down arrow for the
        // whole answer. Positioning is over at answer start — nothing
        // programmatic is (or may be) scrolling anymore.
        programmaticScrollRef.current = false;
        const block = statusBlockRef.current;
        const spacer = bottomSpacerRef.current;
        if (block && spacer) {
          const current = parseFloat(spacer.style.height || '0') || 0;
          spacer.style.height = `${current + block.offsetHeight}px`;
          spacerActiveRef.current = true;
        }
        statusCollapsingRef.current = true;
        setStatusCollapsing(true);
        if (statusCollapseTimer.current) clearTimeout(statusCollapseTimer.current);
        statusCollapseTimer.current = setTimeout(() => {
          statusCollapsingRef.current = false;
          setStatusSteps([]);
          setStatusCollapsing(false);
          // Atomic swap: steps out + first characters in, same React batch —
          // the chat never paints an in-between frame.
          displayedLenRef.current = Math.min(2, currentBotMessage.current.length);
          renderBotText(currentBotMessage.current.slice(0, displayedLenRef.current));
          startReveal();
        }, 460);
      };

      es.onmessage = ({ data }) => {
        if (data === 'end of response') return;

        if (firstChunk) {
          setLoading(false);
          firstChunk = false;
          collapseStatus();
        }
        currentBotMessage.current += data.replace(/<newline>/g, '\n');
        upsertBotMessage();
      };

      // Activity timeline: server-localized steps for what the agent is doing
      // right now. Protocol: {key, label, phase: active|done, detail?,
      // transient?}. Every step PERSISTS (the timeline only ever grows
      // downward — completed steps keep their place and get check-marked by
      // the renderer), upserts by key rewrite a step's label in place
      // (done-updates carry real result numbers), and a filler step
      // ("Processing results…") that already sits mid-list is never
      // re-activated — the timeline moves forward only. Steps only accumulate
      // while we wait for the answer; once text streams, the timeline is
      // collapsing and late events are ignored.
      es.addEventListener('status', ({ data }) => {
        if (!firstChunk) return;
        try {
          const step = JSON.parse(data)?.status;
          if (step && step.key && step.label) {
            setStatusSteps((prev) => {
              const idx = prev.findIndex((s) => s.key === step.key);
              if (idx >= 0) {
                if (step.transient && idx < prev.length - 1) return prev;
                const next = [...prev];
                next[idx] = { ...next[idx], ...step };
                return next;
              }
              return [...prev, step];
            });
          }
        } catch (err) {
          console.warn('Could not parse status event:', err);
        }
      });

      // Structured product cards ride a dedicated SSE event so the text stream
      // stays pure markdown. They normally arrive after the answer text; attach
      // them to the current bot message so the gallery renders under the reply.
      es.addEventListener('products', ({ data }) => {
        try {
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed.products) && parsed.products.length > 0) {
            currentBotProducts.current = parsed.products;
            upsertBotMessage();
          }
        } catch (err) {
          console.warn('Could not parse products event:', err);
        }
      });

      es.addEventListener('end', () => {
        es.close();
        if (hasError) return; // the error path already finalized
        answerDoneRef.current = true;
        // A running fade-out/reveal finishes typing the buffer first
        // (finalizeReply fires from the reveal tick once caught up);
        // finalize immediately only when nothing is animating.
        if (!statusCollapsingRef.current && !revealActiveRef.current) {
          finalizeReply();
        }
      });

      es.addEventListener('error', (e) => {
        hasError = true;
        es.close();

        // Parse the error data (now sent as JSON)
        let errorType = 'default';
        let errorMessage = ERROR_MESSAGES.default;

        try {
          if (e?.data) {
            // Handle legacy format (plain string)
            if (e.data === 'Timeout while generating response') {
              errorType = 'timeout';
            } else if (e.data === 'Internal server error') {
              errorType = 'internal_error';
            } else {
              // Try to parse as JSON (new format)
              const errorData = JSON.parse(e.data);
              errorType = errorData.type || 'default';
            }
            errorMessage = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.default;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default error message
          console.warn('Could not parse error data:', e?.data);
        }

        // Show whatever partial answer was buffered before the error, then
        // the error bubble under it.
        if (currentBotMessage.current) renderBotText(currentBotMessage.current);
        setMessages((m) => [...m, { text: errorMessage, from: 'bot' }]);
        finish();
        resolve();
      });

      // Handle EventSource connection errors (network issues, etc.)
      es.onerror = (e) => {
        // Only handle if not already handled by 'error' event listener
        if (hasError) return;

        // EventSource will auto-reconnect by default - we don't want that for errors
        if (es.readyState === EventSource.CLOSED) {
          hasError = true;
          if (currentBotMessage.current) renderBotText(currentBotMessage.current);
          setMessages((m) => [...m, { text: ERROR_MESSAGES.default, from: 'bot' }]);
          finish();
          resolve();
        } else if (es.readyState === EventSource.CONNECTING) {
          // Connection lost, close and show error
          hasError = true;
          es.close();
          if (currentBotMessage.current) renderBotText(currentBotMessage.current);
          setMessages((m) => [...m, { text: t('chat_err_connection_lost'), from: 'bot' }]);
          finish();
          resolve();
        }
      };
    });
  };

  const LinkRenderer = (p) => (
    <a href={p.href} target="_blank" rel="noopener noreferrer">
      {p.children}
    </a>
  );

  const isBusy = loading || isStreaming;
  // The bot has used up its monthly message allowance. We still render the
  // full chat (faded) but lock it behind an upgrade overlay instead of swapping
  // in a tiny box, so the modal keeps its structure + size.
  const limitReached = !configLoading && (messagePlanLimit - currentMessageCount <= 0);

  // Keep the cursor in the chat input: focus it on open (so the user can type
  // straight away on the Overview playground) and return focus the moment it
  // re-enables after a message (it's disabled while the assistant responds).
  // Depends only on the gating flags — NOT on userInput — so it never steals
  // focus or fights the user mid-typing. preventScroll so focusing the bottom
  // input never yanks the message list / page.
  useEffect(() => {
    if (!open || configLoading || isBusy || limitReached) return;
    if (requirePreChat && !preChatCompleted) return;
    // Never steal focus on mobile: focusing the input pops the on-screen
    // keyboard, and the keyboard's viewport resize fights the rest of the
    // page (it force-dismissed the just-opened sidebar when this fired from
    // the Overview playground right after page load). On phones the user
    // taps the input themselves.
    if (window.innerWidth <= 768) return;
    const el = inputRef.current;
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch { el.focus(); }
  }, [open, configLoading, isBusy, limitReached, requirePreChat, preChatCompleted]);

  const knowledgeCount = typeof currentBot?.knowledge_count === 'number'
    ? currentBot.knowledge_count
    : Array.isArray(currentBot?.knowledge_data)
    ? currentBot.knowledge_data.length
    : 0;
  const showKnowledgeNotice = knowledgeCount === 0;

  // Show no-knowledge modal once per bot when chat opens (after config is loaded)
  useEffect(() => {
    if (open && !configLoading && showKnowledgeNotice && bot?.id && !noKnowledgeShownRef.current.has(bot.id)) {
      noKnowledgeShownRef.current.add(bot.id);
      setShowNoKnowledgeModal(true);
    }
  }, [open, configLoading, showKnowledgeNotice, bot?.id]);

  return (
    open && (
      <>
        <div className={`chat-modal${embedded ? ' embedded' : ''}${isInstagram ? ' chat-modal--instagram' : ''}${isWhatsApp ? ' chat-modal--whatsapp' : ''}`}>
          <div className="chat-modal-overlay" onClick={onClose}></div>
          {configLoading ? (
            <div className="chat-modal-content">
              <div className="chat-modal-loading">
                <FontAwesomeIcon icon={faSpinner} spin className="chat-modal-spinner" />
              </div>
            </div>
          ) : requirePreChat && !preChatCompleted && !limitReached ? (
            /* Pre-chat form - shown when pre-chat is required and not yet completed */
            <div className="chat-modal-content">
              <div className="pre-chat-form-container">
                <div className="pre-chat-form-header">
                  <FontAwesomeIcon icon={faComments} className="pre-chat-form-icon" />
                  <h3>{t('chat_prechat_title')}</h3>
                  <p>{t('chat_prechat_subtitle')}</p>
                  {preChatAnyLanguage && <p className="pre-chat-form-note">{preChatTexts(i18n.language).anyLanguage}</p>}
                </div>
                <div className="pre-chat-form-fields">
                  {preChatFields.map((field) => (
                    <div key={field.id} className="pre-chat-field">
                      <label htmlFor={`prechat-${field.name}`}>{preChatLabel(field, i18n.language)}</label>
                      <input
                        id={`prechat-${field.name}`}
                        type={field.name.toLowerCase().includes('email') ? 'email' :
                              field.name.toLowerCase().includes('phone') || field.name.toLowerCase().includes('telephone') ? 'tel' : 'text'}
                        value={preChatFormData[field.name] || ''}
                        onChange={(e) => handlePreChatFieldChange(field.name, e.target.value)}
                        placeholder={preChatLabel(field, i18n.language)}
                        disabled={preChatSubmitting}
                      />
                    </div>
                  ))}
                </div>
                <button
                  className="pre-chat-submit-btn"
                  onClick={handlePreChatSubmit}
                  disabled={!isPreChatFormValid() || preChatSubmitting}
                >
                  {preChatSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> {t('chat_prechat_starting')}
                    </>
                  ) : (
                    t('chat_prechat_start')
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className={`chat-modal-content${limitReached ? ' is-locked' : ''}`}>
              <div className="chat-modal-header">
                <div className="chat-modal-header-left">
                  {isMessenger ? instagramAvatar : <span className="chat-modal-header-ic">
                    <FontAwesomeIcon icon={faComments} />
                  </span>}
                  <span className="chat-modal-header-text">
                    <span className="chat-modal-header-title">{isMessenger ? instagramName : t('chat_playground_title')}</span>
                    {embedded && (
                      <span className="chat-modal-header-sub">
                        {isInstagram ? <><img className="chat-instagram-sub-icon" src="/brand/instagram/Instagram_Glyph_Black.svg" alt="" aria-hidden="true" /> {t('chat_instagram_preview')}</>
                          : isWhatsApp ? <><img className="chat-whatsapp-sub-icon" src="/whatsapp-logo.webp" alt="" aria-hidden="true" /> {t('chat_whatsapp_preview')}</>
                          : t('chat_playground_subtitle')}
                      </span>
                    )}
                  </span>
                </div>
                <div className="chat-modal-header-actions">
                  <button
                    type="button"
                    className="chat-modal-clear-btn"
                    onClick={clearChat}
                    disabled={isBusy}
                    aria-label={t('chat_clear_aria')}
                  >
                    <FontAwesomeIcon icon={faRotateLeft} /> {t('studio_hero_apps_clear')}
                  </button>
                  <button className="chat-modal-close-btn" onClick={onClose} aria-label={t('chat_close_aria')}>
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              </div>
              <div className="chat-parent">
                <div className="logo-messages-container">
                  <div className="message-container" ref={messageContainerRef} onScroll={handleContainerScroll}>
                    {messages.map((msg, idx) => {
                      const showBotIcon =
                        msg.from === 'bot' && (idx === 0 || messages[idx - 1].from !== 'bot');

                      const firstUserIndex = messages.findIndex(m => m.from === 'user');
                      const isWelcomeBotMessage =
                        msg.from === 'bot' && (firstUserIndex === -1 ? true : idx < firstUserIndex);

                      const isLast = idx === messages.length - 1;
                      const showQAForThisBotMsg =
                        showImprove &&
                        msg.from === 'bot' &&
                        !isWelcomeBotMessage &&
                        (!isLast || (isLast && !isStreaming));

                      const previousUser = showQAForThisBotMsg
                        ? [...messages.slice(0, idx)].reverse().find(m => m.from === 'user')
                        : null;

                      return (
                        <div key={idx} className={`message ${msg.from === 'user' ? 'user-message' : 'bot-message'}`}>
                          {showBotIcon && (
                            <div className="scf-icon-container">
                              {isMessenger ? instagramAvatar : <div className="bot-left-icon">
                                <div className="bot-left-icon-text">
                                  <p style={{ fontSize: '11px', lineHeight: '1.05' }}>AI</p>
                                  <p>{t('chat_bot_icon_agent')}</p>
                                </div>
                              </div>}
                            </div>
                          )}
                          <div className={msg.from === 'user' ? 'user-chat-text' : 'bot-chat-text'}>
                            <ReactMarkdown
                              remarkPlugins={[[remarkGfm, {}], [remarkBreaks, {}]]}
                              rehypePlugins={[rehypeRaw]}
                              components={{ a: LinkRenderer }}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          </div>

                          {/* In-chat product gallery: structured cards the agent
                              attached to this reply, rendered directly under the text. */}
                          {msg.from === 'bot' && Array.isArray(msg.products) && msg.products.length > 0 && (
                            <div className="ub-pc-inline">
                              <ProductCarousel products={msg.products} />
                            </div>
                          )}

                          {/* CTA below the response: hand the flagged reply to the
                              Agent-Studio builder as a prefilled message. */}
                          {showQAForThisBotMsg && (
                            <div className="qa-cta">
                              <div className='qa-span-button-container'>
                                <span>{t('chat_qa_cta_prompt')}</span>
                                <button
                                  className="add-faq-button"
                                  onClick={() =>
                                    openInStudio(
                                      previousUser?.text || '',
                                      msg.text
                                    )
                                  }
                                >
                                  <FontAwesomeIcon icon={faWandMagicSparkles} className='qa-lighbulb-icon'/> {t('chat_fix_btn')}
                                </button>
                              </div>
                            </div>
                          )}

                          {msg.from === 'user' && !isMessenger && (
                            <div className="user-icon-container">
                              {/* ABSOLUTE, not '../': the relative form resolved against
                                  the current URL, so it only happened to work on the
                                  portal's shallow routes and 404'd wherever the page sits
                                  deeper — e.g. the Shopify onboarding at
                                  /api/shopify/admin asked for /api/user-icon.png and
                                  rendered a broken-image glyph. The asset lives in the
                                  portal's public root, which every surface that embeds
                                  this component shares (same origin). */}
                              <img src="/user-icon.png" alt={t('chat_user_icon_alt')} className="user-icon" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Perplexity-style activity timeline, rendered as a real
                        bot message (same avatar structure) so the hand-off to
                        the streaming answer feels seamless. Present from the
                        very first frame after send: before the first server
                        `status` event lands, a local "Analyzing…" placeholder
                        (same key, so the server event upserts it) replaces the
                        old loading dots entirely. Every step persists — done
                        steps check off in place, ONLY the last row ever pulses
                        (never a dead timeline) — rows have a fixed height and
                        never wrap, so the chat never jumps. On answer start the
                        rows roll away upward, staggered top-first. */}
                    {(() => {
                      const displaySteps =
                        statusSteps.length > 0
                          ? statusSteps
                          : loading
                            ? [{ key: 'analyzing', label: t('chat_status_analyzing'), phase: 'active' }]
                            : [];
                      if (displaySteps.length === 0) return null;
                      return (
                        <div ref={statusBlockRef} className="message bot-message chat-status-message">
                          {/* The avatar lives OUTSIDE the folding bubble on
                              purpose: it must not move or fade a single pixel
                              during the roll-up — the answer message that
                              replaces this block mounts the identical avatar
                              markup in the identical flow position. */}
                          <div className="scf-icon-container">
                            {isMessenger ? instagramAvatar : <div className="bot-left-icon">
                              <div className="bot-left-icon-text">
                                <p style={{ fontSize: '11px', lineHeight: '1.05' }}>AI</p>
                                <p>{t('chat_bot_icon_agent')}</p>
                              </div>
                            </div>}
                          </div>
                          <div className={`chat-status-timeline${statusCollapsing ? ' collapsing' : ''}`}>
                            {displaySteps.map((s, i) => {
                              const isLast = i === displaySteps.length - 1;
                              const isActive = isLast && !statusCollapsing;
                              return (
                                <div
                                  key={s.key}
                                  className={`chat-status-step ${isActive ? 'active' : 'done'}`}
                                >
                                  <span className="chat-status-icon">
                                    {isActive ? (
                                      <span className="chat-status-pulse" />
                                    ) : (
                                      <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                                        <path
                                          d="M2 6.2 4.8 9 10 3.4"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.8"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </span>
                                  <span className="chat-status-label">{s.label}</span>
                                  {s.detail && <StatusDetail text={s.detail} />}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    <div ref={bottomSpacerRef} style={{ flexShrink: 0 }} />
                    <div ref={messagesEndRef} />
                  </div>
                  {showScrollDown && (
                    <button
                      className="scroll-to-bottom-btn"
                      onClick={scrollToBottom}
                      aria-label={t('chat_scroll_latest_aria')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="14" height="14">
                        <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" fill="currentColor"/>
                      </svg>
                    </button>
                  )}
                </div>

                {/* "Talk to a human" affordance — mirrors the public widget so
                    bot owners can preview placement, but clicking opens an
                    info modal instead of starting a real hand-off (see
                    LiveAgentInfoModal for the rationale). Rendered ABOVE the
                    predefined-questions row to match widget ordering. */}
                {!isMessenger && (liveCtaAlways || (liveSettings.show_request_button && agentAvailable)) && (
                  <div className="chat-modal-live-actions">
                    <button
                      type="button"
                      className="chat-modal-live-action-btn"
                      onClick={() => (onLiveCta ? onLiveCta() : setShowLiveAgentInfoModal(true))}
                    >
                      <FontAwesomeIcon icon={faHeadset} />
                      <span>{liveSettings.request_button_text || t('chat_talk_to_human')}</span>
                    </button>
                  </div>
                )}

                {predefinedQuestions.length > 0 && (
                  <div
                    className="predefined-container"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap-reverse',
                      justifyContent: 'flex-end',
                      gap: '8px',
                      padding: '6px 10px',
                      paddingBottom: '0',
                      marginBottom: '4px'
                    }}
                  >
                    {predefinedQuestions.map((q, i) => (
                      <div
                        key={i}
                        className={`predefined-question${!userAsked && i === animateIndex ? ' jump' : ''}${isBusy ? ' is-disabled' : ''}`}
                        onClick={() => {
                          if (!isBusy) sendMessage(q);
                        }}
                        style={{
                          flex: '0 0 auto',
                          background: 'transparent',
                          color: '#333',
                          borderRadius: '20px',
                          padding: '6px 14px',
                          fontSize: '16px',
                          whiteSpace: 'nowrap',
                          cursor: isBusy ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (!isBusy) {
                            e.currentTarget.style.backgroundColor = '#f4f4f4ff';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        tabIndex={isBusy ? -1 : 0}
                        aria-disabled={isBusy}
                      >
                        {q}
                      </div>
                    ))}
                  </div>
                )}

                <div className="chat-input" style={isWhatsApp ? undefined : { backgroundColor: 'white', borderTop: 'none' }}>
                  <textarea
                    ref={(el) => {
                      inputRef.current = el;
                      if (el) {
                        el.style.height = 'auto';
                        const lineHeight = 22; // ~16px * 1.4 line-height
                        const maxLines = 5;
                        const padding = 12; // 6px top + 6px bottom
                        const maxHeight = (lineHeight * maxLines) + padding;
                        const minHeight = lineHeight + padding; // single line + padding
                        const newHeight = el.value ? Math.min(el.scrollHeight, maxHeight) : minHeight;
                        el.style.height = newHeight + 'px';
                        // Only show scrollbar when content exceeds max height
                        if (el.scrollHeight > maxHeight) {
                          el.classList.add('has-overflow');
                        } else {
                          el.classList.remove('has-overflow');
                        }
                      }
                    }}
                    rows={1}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isBusy && !limitReached) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={isBusy || limitReached}
                    aria-disabled={isBusy || limitReached}
                    placeholder={t('chat_input_ph')}
                  />
                  {localStorage.getItem('user_id') === '21' && (
                    <button
                      onClick={toggleVoice}
                      disabled={isBusy && voiceState === 'idle'}
                      className={`voice-button${voiceState !== 'idle' ? ' voice-active' : ''}`}
                      aria-label={t('chat_voice_agent_aria')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span className="voice-tooltip">{t('chat_voice_agent_aria')}</span>
                    </button>
                  )}
                  <button onClick={sendMessage} disabled={!userInput.trim() || isBusy || limitReached} className="input-button" aria-label={isInstagram ? t('live_inbox_send') : undefined} aria-disabled={!userInput.trim() || isBusy || limitReached}>
                    {isInstagram ? t('live_inbox_send') : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="send-icon">
                      <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z"/>
                    </svg>}
                  </button>
                </div>
                {showKnowledgeNotice && (
                  <div className="chat-knowledge-banner" role="status">
                    <div className="chat-knowledge-banner-textrow">
                      <FontAwesomeIcon icon={faTriangleExclamation} className="chat-knowledge-banner-icon" />
                      <p>
                        {t('chat_no_knowledge_banner')}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="manage-knowledge-button"
                      onClick={() => {
                        // Ensure modal closes (especially when opened from floating widget) before navigation
                        if (typeof onClose === 'function') {
                          try { onClose(); } catch {}
                        }
                        navigate(`/settings?botId=${bot.id}&tab=knowledge`);
                      }}
                    >
                      {t('chat_manage_knowledge')}
                    </button>
                  </div>
                )}
                {/* Messages left + usage bar + plan CTA. Hidden in the
                    embedded playground — the Overview already shows AI
                    responses / plan / upgrade alongside it. */}
                {!embedded && (() => {
                  const remaining = messagePlanLimit - localMessageCount; // can be negative
                  const remainingClamped = Math.max(remaining, 0);
                  const percent = Math.min((remainingClamped / messagePlanLimit) * 100, 100);
                  const ctaLabel = planName === 'Free' ? t('upgrade') : t('chat_usage_modify');

                  return (
                    <div className="message-usage-row">
                      <div className="message-usage-grid">
                        {/* Row 1: left = messages left, right = plan name (same height) */}
                        <span
                          className={`message-usage-text${remaining <= 0 ? ' message-usage-text--danger' : ''}`}
                          aria-live="polite"
                        >
                          {inCredits ? t('chat_credits_left') : t('chat_messages_left')} <span style={{color: '#5616ea', fontWeight: '600'}}>{remaining} / {messagePlanLimit}</span>
                        </span>
                        <span className="message-plan-name">{t('chat_plan_label')} <span style={{color: '#5616ea', fontWeight: '600'}}>{planName}</span></span>

                        {/* Row 2: left = bar (stretches), right = button (right next to the line) */}
                        <div className="message-usage-bar" aria-hidden="true">
                          <div className="message-usage-fill" style={{ width: `${percent}%` }} />
                        </div>

                        <button
                          type="button"
                          className={`message-usage-cta${remaining <= 0 ? ' message-usage-cta--attention' : ''}`}
                          onClick={() => openSubscriptionModalFromChat(bot)}
                        >
                          {ctaLabel}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {voiceState !== 'idle' && (
                <div
                  className="voice-overlay"
                  onClick={stopAndSpeakAgain}
                  style={{ cursor: voiceState === 'listening' || voiceState === 'initializing' ? 'default' : 'pointer' }}
                >
                  <div className="voice-overlay-bg" />
                  <div className="voice-status-dot" />

                  {voiceState === 'initializing' ? (
                    <div className="voice-initializing-spinner">
                      <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                  ) : (
                  <div className="voice-visualizer-container">
                    <div
                      className={`voice-blob ${voiceState === 'listening' ? 'voice-blob-listening' : ''} ${voiceState === 'thinking' ? 'voice-blob-thinking' : ''} ${voiceState === 'speaking' ? 'voice-blob-speaking' : ''}`}
                      style={{
                        transform: voiceState === 'thinking'
                          ? `scale(${1 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.04})`
                          : `scale(${1 + audioLevel * 0.3})`,
                        transition: 'transform 0.12s ease-out',
                      }}
                    >
                      <div className="voice-blob-inner" />
                      <div
                        className="voice-blob-ring voice-blob-ring-1"
                        style={voiceState === 'thinking' ? {
                          transform: `scale(${1.1 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.05})`,
                          opacity: 0.3 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.08,
                          transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                        } : {}}
                      />
                      <div
                        className="voice-blob-ring voice-blob-ring-2"
                        style={voiceState === 'thinking' ? {
                          transform: `scale(${1.2 + (processingPulse < 4 ? 4 - processingPulse : processingPulse - 4) * 0.05})`,
                          opacity: 0.2 + (processingPulse < 4 ? 4 - processingPulse : processingPulse - 4) * 0.06,
                          transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                        } : {}}
                      />
                      <div
                        className="voice-blob-ring voice-blob-ring-3"
                        style={voiceState === 'thinking' ? {
                          transform: `scale(${1.3 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.04})`,
                          opacity: 0.15 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.05,
                          transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                        } : {}}
                      />
                    </div>
                  </div>
                  )}
                  <div className="voice-status-text">
                    {voiceState === 'initializing' && t('chat_voice_connecting')}
                    {voiceState === 'listening' && t('chat_voice_listening')}
                    {voiceState === 'thinking' && t('chat_voice_processing')}
                    {voiceState === 'speaking' && t('chat_voice_speaking')}
                  </div>
                  {voiceState === 'initializing' && (
                    <div className="voice-hint-text">{t('chat_voice_setup_mic')}</div>
                  )}
                  {(voiceState === 'thinking' || voiceState === 'speaking') && (
                    <div className="voice-hint-text">{t('chat_voice_tap_interrupt')}</div>
                  )}
                  <div className="voice-controls">
                    <button
                      className="voice-control-btn voice-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        stopVoice();
                      }}
                      aria-label={t('chat_voice_close_aria')}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                </div>
              )}
              {limitReached && (
                <div className="chat-locked-overlay">
                  {!embedded && (
                    <button
                      type="button"
                      className="chat-locked-close"
                      onClick={onClose}
                      aria-label={t('chat_close_aria')}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                  <div className="chat-locked-card">
                    <span className="chat-locked-badge">
                      <FontAwesomeIcon icon={faLock} />
                    </span>
                    <h3 className="chat-locked-title">{inCredits ? t('chat_credits_used_up_title') : t('chat_limit_reached_title')}</h3>
                    <p className="chat-locked-sub">
                      {inCredits ? t('chat_credits_used_up_sub') : t('chat_limit_reached_sub')}
                    </p>
                    <button className="upgrade-button chat-locked-btn" onClick={() => openSubscriptionModalFromChat(bot)}>
                      {t('upgrade')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* No-knowledge modal */}
        <NoKnowledgeModal
          open={showNoKnowledgeModal}
          onClose={() => setShowNoKnowledgeModal(false)}
          onManageKnowledge={() => {
            setShowNoKnowledgeModal(false);
            if (typeof onClose === 'function') {
              try { onClose(); } catch {}
            }
            navigate(`/settings?botId=${bot.id}&tab=knowledge`);
          }}
        />

        {/* Live-agent info modal \u2014 explains why the test chat doesn't run a
            real hand-off and offers two productive next steps. */}
        <LiveAgentInfoModal
          open={showLiveAgentInfoModal}
          onClose={() => setShowLiveAgentInfoModal(false)}
          publicChatUrl={bot?.id ? `https://chat.ultimo-bots.com/${bot.id}` : ''}
          onOpenLiveSessions={() => {
            setShowLiveAgentInfoModal(false);
            if (typeof onClose === 'function') {
              try { onClose(); } catch {}
            }
            navigate('/live');
          }}
        />

        {/* FAQ modal mount */}
        <AddFaqFromHistoryModal
          open={faqModalOpen}
          onClose={() => setFaqModalOpen(false)}
          onSaved={async () => {
            setFaqModalOpen(false);
            // Reset chat to initial state with welcome message
            setUserAsked(false);
            setSessionId(`portal-${Date.now()}-${Math.floor(Math.random() * 10000) + 1}`);
            setConfigLoading(true);
            try {
              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}widget_configuration/${bot.id}`);
              const data = await res.json();
              const welcomeMessages = Array.isArray(data.welcome_message)
                ? data.welcome_message
                : data.welcome_message
                ? [data.welcome_message]
                : [t('chat_welcome_fallback')];
              setMessages(welcomeMessages.map((msg) => ({ text: msg, from: 'bot' })));
            } catch {
              setMessages([{ text: t('chat_welcome_load_failed'), from: 'bot' }]);
            } finally {
              setConfigLoading(false);
            }
          }}
          question={prefilledQuestion}
          answer={prefilledAnswer}
          botId={prefilledBotId}
        />
      </>
    )
  );
};

export default ChatModal;
